/**
 * EmailCaptureCard — optional email opt-in shown beneath the calculator
 * result. Submitting persists to `calculator_leads` with `opt_in=true`.
 * The PRD requires this to be unchecked by default and never required
 * to access the result — we honor both rules here.
 */
import { useState } from "react";
import { Loader2, Mail, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

import { captureCalculatorLead } from "@/lib/calculatorApi";

export default function EmailCaptureCard({ slug, inputs, result }) {
    const [email, setEmail] = useState("");
    const [optIn, setOptIn] = useState(false);
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!email || !optIn) return;
        setBusy(true);
        try {
            await captureCalculatorLead({ slug, email, inputs, result });
            setDone(true);
            toast.success("Got it — keep an eye out for the next inspection tip.");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't save right now. Try again in a moment.");
        } finally {
            setBusy(false);
        }
    };

    if (done) {
        return (
            <div
                className="rounded-lg border border-[var(--bl-green)]/30 bg-[var(--bl-green)]/8 p-5"
                data-testid="email-capture-success"
            >
                <div className="flex items-center gap-2 text-[var(--bl-green)] text-sm">
                    <CheckCircle2 className="h-4 w-4" />
                    You&apos;re on the list. We&apos;ll keep it short and useful.
                </div>
            </div>
        );
    }

    return (
        <div
            className="rounded-xl border-2 border-bl-primary/30 bg-[var(--bl-teal-tint)] p-5 sm:p-6 space-y-3"
            data-testid="email-capture-card"
        >
            <div className="flex items-start gap-2.5 text-bl">
                <div className="w-9 h-9 rounded-full bg-bl-primary/15 text-bl-primary flex items-center justify-center shrink-0">
                    <Mail className="h-4 w-4" />
                </div>
                <div className="min-w-0">
                    <div className="font-display font-semibold text-base text-bl leading-snug">
                        Want this result sent to your inbox?
                    </div>
                    <p className="text-xs text-bl-muted mt-1 leading-relaxed">
                        We&apos;ll email you a copy of these numbers plus occasional
                        buyer-side inspection tips. Unsubscribe anytime.
                    </p>
                </div>
            </div>
            <form onSubmit={submit} className="space-y-2.5">
                <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@email.com"
                    className="bl-input w-full text-sm"
                    required
                    data-testid="email-capture-input"
                />
                <label className="flex items-start gap-2 text-xs text-bl-muted cursor-pointer">
                    <input
                        type="checkbox"
                        checked={optIn}
                        onChange={(e) => setOptIn(e.target.checked)}
                        className="mt-0.5 h-3.5 w-3.5 rounded border-bl/40 text-bl-teal focus:ring-bl-teal"
                        data-testid="email-capture-optin"
                    />
                    <span>
                        Yes, send me my result + occasional buyer-side inspection
                        tips. I can unsubscribe at any time.
                    </span>
                </label>
                <button
                    type="submit"
                    disabled={busy || !email || !optIn}
                    className="bl-btn-primary text-sm w-full disabled:opacity-50"
                    data-testid="email-capture-submit"
                >
                    {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                    Send to my inbox
                </button>
            </form>
        </div>
    );
}
